import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';

export default function LoginPage() {
  const { login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === 'admin') navigate('/admin');
      else navigate('/partner');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your Partners Portal account" testId="login-page">
      {error && (
        <div role="alert" className="mb-4 p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm" data-testid="login-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-ink font-medium">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            data-testid="login-email-input"
            className="h-11 bg-surface border-contrast/[0.12] shadow-sm focus-visible:ring-2 focus-visible:ring-brand/20 focus-visible:border-brand"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-ink font-medium">Password</Label>
            <Link to="/forgot-password" className="text-sm text-brand hover:text-brand-soft underline-offset-4 hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="login-password-input"
              className="h-11 bg-surface border-contrast/[0.12] shadow-sm focus-visible:ring-2 focus-visible:ring-brand/20 focus-visible:border-brand pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-2"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-button"
          className="w-full h-11 bg-brand hover:bg-brand-soft text-white font-medium rounded-lg transition-all shadow-[0_8px_20px_-8px_rgba(37,99,235,0.55)] hover:shadow-[0_10px_24px_-8px_rgba(37,99,235,0.65)]"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-ink-2">
        New partner?{' '}
        <Link to="/register" className="text-brand hover:text-brand-soft font-medium underline-offset-4 hover:underline" data-testid="register-link">
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}
